import { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import CollectionsIcon from "@mui/icons-material/Collections";
import FolderIcon from "@mui/icons-material/Folder";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckIcon from "@mui/icons-material/Check";
import { useTheme } from "@mui/material/styles";
import { AuthContext } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeContext";
import { useTour } from "../context/TourContext";
import { getMediaUrl } from "../service/mediaApi";
import Btn from "./Btn";

const btnBase = {
  padding: "3px 8px",
  borderRadius: 5,
  border: "1px solid rgba(255,255,255,0.3)",
  cursor: "pointer",
  fontSize: "0.72rem",
  background: "transparent",
  color: "inherit",
};
const btnActive = { ...btnBase, background: "rgba(255,255,255,0.2)", fontWeight: 700 };

const INTENSITIES = [["low", "Doux"], ["medium", "Standard"], ["high", "Contrasté"]];

function NavLink({ path, icon, label, currentPath }) {
  const active = currentPath === path || currentPath.startsWith(path + "/");
  return (
    <Btn path={path} sx={{ width: "auto", flex: "none" }} msg={
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3,
        borderRadius: 1,
        backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
        transition: "background-color 0.15s",
      }}>
        {icon}
        <Typography variant="body2" sx={{ color: "white", whiteSpace: "nowrap", fontSize: "0.85rem" }}>
          {label}
        </Typography>
      </Box>
    } />
  );
}

export default function Header() {
  const { state: user, setState: setUser } = useContext(AuthContext);
  const isConnected = !!user;
  const muiTheme = useTheme();
  const { mode, intensity, changeMode, changeIntensity } = useThemeMode();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [intensityOpen, setIntensityOpen] = useState(false);

  const handleLogout = () => {
    fetch("/api/logout", { method: "POST", credentials: "include" }).then(() => {
      setUser(null);
      navigate("/");
    });
  };

  const iconSx = { color: "white", fontSize: "1.1rem" };
  const drawerBg = muiTheme.custom?.myheader?.main ?? "#0f172a";

  const NAV_LINKS = [
    { path: "/gallery", icon: <CollectionsIcon fontSize="small" />, label: "Ma galerie", tourId: "tour-nav-gallery" },
    { path: "/albums", icon: <FolderIcon fontSize="small" />, label: "Mes albums", tourId: "tour-nav-albums" },
    { path: "/shared-with-me", icon: <FolderSharedIcon fontSize="small" />, label: "Partagé avec moi", tourId: "tour-nav-shared" },
  ];

  const DrawerContent = () => (
    <Box
      sx={{
        width: 270,
        height: "100%",
        backgroundColor: drawerBg,
        color: "white",
        display: "flex",
        flexDirection: "column",
        py: 1,
      }}
      role="presentation"
    >
      {/* En-tête */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5 }}>
        <img src="/favicon.svg" alt="logo" style={{ width: 28, height: 28 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "white" }}>
          Family Share
        </Typography>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      <List dense sx={{ flex: 1 }}>
        {/* Navigation */}
        {isConnected && NAV_LINKS.map(({ path, icon, label, tourId }) => {
          const active = location.pathname === path || location.pathname.startsWith(path + "/");
          return (
            <ListItemButton
              key={path}
              data-tour={tourId}
              onClick={() => { navigate(path); setDrawerOpen(false); }}
              sx={{
                color: "white",
                backgroundColor: active ? "rgba(255,255,255,0.1)" : "transparent",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
              }}
            >
              <ListItemIcon sx={{ color: "white", minWidth: 36 }}>{icon}</ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.9rem" }} />
            </ListItemButton>
          );
        })}

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 0.5 }} />

        {/* Dark / Light (toggle direct) */}
        <ListItemButton
          onClick={() => changeMode(mode === "dark" ? "light" : "dark")}
          sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
        >
          <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
            {mode === "dark" ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={mode === "dark" ? "◑ Sombre" : "◐ Clair"}
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </ListItemButton>

        {/* Intensité */}
        <ListItemButton
          onClick={() => setIntensityOpen((v) => !v)}
          sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
        >
          <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={INTENSITIES.find(([v]) => v === intensity)?.[1] ?? intensity}
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
          {intensityOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </ListItemButton>
        <Collapse in={intensityOpen} timeout="auto" unmountOnExit>
          <List dense disablePadding>
            {INTENSITIES.map(([v, label]) => (
              <ListItemButton
                key={v}
                onClick={() => { changeIntensity(v); setIntensityOpen(false); }}
                sx={{
                  pl: 4,
                  color: intensity === v ? "#93c5fd" : "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
                }}
              >
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: "0.85rem" }} />
                {intensity === v && <CheckIcon fontSize="small" sx={{ color: "#93c5fd" }} />}
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 0.5 }} />

        {/* Aide */}
        {isConnected && (
          <ListItemButton
            onClick={() => { startTour(); setDrawerOpen(false); }}
            sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
              <HelpOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Visite guidée" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}

        {/* Profil */}
        {isConnected && (
          <ListItemButton
            onClick={() => { navigate("/profile"); setDrawerOpen(false); }}
            sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Avatar
                src={user?.users_avatar_public_id ? getMediaUrl(user.users_avatar_public_id) : undefined}
                sx={{ width: 24, height: 24, fontSize: "0.75rem", backgroundColor: "rgba(255,255,255,0.2)" }}
              >
                {user?.users_pseudo?.[0]?.toUpperCase()}
              </Avatar>
            </ListItemIcon>
            <ListItemText primary="Mon profil" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* Déconnexion / Connexion */}
      <List dense>
        {isConnected ? (
          <ListItemButton
            onClick={() => { handleLogout(); setDrawerOpen(false); }}
            sx={{ color: "#f87171", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: "#f87171", minWidth: 36 }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Déconnexion" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        ) : (
          <ListItemButton
            onClick={() => { navigate("/connexion"); setDrawerOpen(false); }}
            sx={{ color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.07)" } }}
          >
            <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
              <LoginIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Connexion" primaryTypographyProps={{ fontSize: "0.9rem" }} />
          </ListItemButton>
        )}
      </List>
    </Box>
  );

  /* ── VERSION MOBILE ── */
  if (isMobile) {
    return (
      <>
        <AppBar position="sticky" elevation={0} sx={{ backgroundColor: muiTheme.custom.myheader.main }}>
          <Toolbar sx={{ justifyContent: "space-between", minHeight: "48px !important", px: 1.5 }}>
            {/* Logo — ouvre le drawer */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              size="small"
              sx={{ p: 0.5 }}
              aria-label="menu"
            >
              <img src="/favicon.svg" alt="logo" style={{ width: 32, height: 32 }} />
            </IconButton>

            {/* Titre centré */}
            <Typography
              variant="subtitle1"
              sx={{
                color: "white",
                fontWeight: "bold",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              Family Share
            </Typography>

            {/* Avatar (raccourci rapide) */}
            {isConnected && (
              <IconButton onClick={() => navigate("/profile")} size="small" title="Mon profil">
                <Avatar
                  src={user?.users_avatar_public_id ? getMediaUrl(user.users_avatar_public_id) : undefined}
                  sx={{ width: 28, height: 28, fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  {user?.users_pseudo?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
            )}
            {!isConnected && <Box sx={{ width: 40 }} />}
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { backgroundColor: drawerBg } }}
        >
          <DrawerContent />
        </Drawer>
      </>
    );
  }

  /* ── VERSION DESKTOP ── */
  return (
    <AppBar position="sticky" elevation={0} sx={{ backgroundColor: muiTheme.custom.myheader.main }}>
      <Toolbar sx={{ justifyContent: "space-between", gap: 1, flexWrap: "wrap", minHeight: "48px !important" }}>

        {/* Titre + nav */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Btn path="/" sx={{ width: "auto", flex: "none" }} msg={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, px: 1 }}>
              <img src="/favicon.svg" alt="" style={{ width: 28, height: 28 }} />
              <Typography variant="h6" sx={{ color: "white", fontWeight: "bold", whiteSpace: "nowrap" }}>
                Family Share
              </Typography>
            </Box>
          } />
          {isConnected && (
            <>
              <Box data-tour="tour-nav-gallery">
                <NavLink path="/gallery" currentPath={location.pathname} icon={<CollectionsIcon sx={iconSx} />} label="Ma galerie" />
              </Box>
              <Box data-tour="tour-nav-albums">
                <NavLink path="/albums" currentPath={location.pathname} icon={<FolderIcon sx={iconSx} />} label="Mes albums" />
              </Box>
              <Box data-tour="tour-nav-shared">
                <NavLink path="/shared-with-me" currentPath={location.pathname} icon={<FolderSharedIcon sx={iconSx} />} label="Partagé avec moi" />
              </Box>
            </>
          )}
        </Box>

        {/* Contrôles thème */}
        <Box sx={{ display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" }}>
          {["dark", "light"].map((m) => (
            <button key={m} onClick={() => changeMode(m)} style={mode === m ? btnActive : btnBase}>
              {m === "dark" ? "◑ Sombre" : "◐ Clair"}
            </button>
          ))}
          <span style={{ opacity: 0.3, fontSize: "0.7rem" }}>|</span>
          {INTENSITIES.map(([v, l]) => (
            <button key={v} onClick={() => changeIntensity(v)} style={intensity === v ? btnActive : btnBase}>
              {l}
            </button>
          ))}
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {isConnected ? (
            <>
              <IconButton onClick={startTour} size="small" title="Visite guidée">
                <HelpOutlineIcon sx={{ color: "white" }} />
              </IconButton>
              <IconButton onClick={() => navigate("/profile")} size="small" title="Mon profil">
                <Avatar
                  src={user?.users_avatar_public_id ? getMediaUrl(user.users_avatar_public_id) : undefined}
                  sx={{ width: 28, height: 28, fontSize: "0.8rem", backgroundColor: "rgba(255,255,255,0.2)" }}
                >
                  {user?.users_pseudo?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <IconButton onClick={handleLogout} size="small">
                <LogoutIcon sx={{ color: "white" }} />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={() => navigate("/connexion")} size="small">
              <LoginIcon sx={{ color: "white" }} />
            </IconButton>
          )}
        </Box>

      </Toolbar>
    </AppBar>
  );
}
